import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button } from 'react-bootstrap';
import './Careers.css';
import SEO from './SEO.js';

const Careers = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
    resumeLink: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the form data to your backend
    console.log('Form submitted:', formData);
    // Reset form after submission
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      position: '',
      resumeLink: ''
    });
    alert('Application submitted successfully!');
  };

  const positions = [
    {
      id: 1,
      title: 'Physiotherapy',
      description: 'Join our team of skilled physiotherapists to help patients recover from injuries and improve mobility. You will work with patients of all ages, providing personalized treatment plans and rehabilitation exercises.',
      requirements: 'Bachelor\'s degree in Physiotherapy, 2+ years of experience, and valid license.',
      image: '/images/physiotherapy.jpg'
    },
    {
      id: 2,
      title: 'Nursing',
      description: 'As a nurse at Caremetto, you will provide compassionate care to patients in their homes. Your responsibilities include administering medications, monitoring vital signs, and coordinating with doctors.',
      requirements: 'Registered Nurse certification, 3+ years of clinical experience, and excellent communication skills.',
      image: '/images/nursing.jpg'
    },
    {
      id: 3,
      title: 'Care Giver',
      description: 'Our caregivers provide essential support to patients who need assistance with daily activities. You will help with personal care, meal preparation, medication reminders, and companionship.',
      requirements: 'Caregiving certification, 1+ year experience in home care, and a compassionate attitude.',
      image: '/images/care-giver.jpg'
    },
    {
      id: 4,
      title: 'Doctor',
      description: 'We are looking for dedicated doctors to join our home healthcare team. You will conduct home visits, provide consultations, and develop treatment plans for patients with various medical conditions.',
      requirements: 'MD degree, valid medical license, 5+ years of clinical experience, and excellent bedside manner.',
      image: '/images/doctor-consult.jpg'
    },
    {
      id: 5,
      title: 'Lab',
      description: 'Join our mobile laboratory team to collect samples and conduct tests in patients\'s homes. You will ensure accurate testing procedures and timely reporting of results to healthcare providers.',
      requirements: 'Medical Laboratory Technician certification, experience with phlebotomy, and attention to detail.',
      image: '/images/lab.jpg'
    },
    {
      id: 6,
      title: 'Mother & Baby Care',
      description: 'Provide specialized care for new mothers and infants in their homes. You will offer support with breastfeeding, newborn care, postpartum recovery, and maternal health monitoring.',
      requirements: 'Nursing or midwifery qualification, experience in maternal-child health, and patience with new parents.',
      image: '/images/baby-care.jpg'
    }
  ];

  return (
    <div className="careers-section" id="careers">
      <SEO 
        title="Careers at Caremetto Healthcare - Join Our Team"
        description="Join Caremetto Healthcare and build a rewarding career in home healthcare. Hiring nurses, physiotherapists, caregivers, and doctors in Bangalore."
        keywords="healthcare jobs Bangalore, nursing jobs, physiotherapist jobs, caregiver vacancies, medical careers Bangalore"
      />
      <Container>
        <div className="section-header text-center">
          <h2>Join Our Team</h2>
          <p>Be part of our mission to provide exceptional healthcare services at home</p>
        </div>

        <Row className="mb-5">
          <Col lg={12}>
            <div className="careers-intro">
              <h3>Why Work With Us?</h3>
              <p>At Caremetto, we're dedicated to improving lives through compassionate home healthcare. When you join our team, you become part of a supportive community that values growth, innovation, and patient-centered care.</p>
              <div className="benefits">
                <Row>
                  <Col md={4}>
                    <div className="benefit-item">
                      <i className="fas fa-medal"></i>
                      <h4>Professional Growth</h4>
                      <p>Continuous learning opportunities and career advancement</p>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="benefit-item">
                      <i className="fas fa-users"></i>
                      <h4>Supportive Team</h4>
                      <p>Work with dedicated professionals who share your passion</p>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="benefit-item">
                      <i className="fas fa-heart"></i>
                      <h4>Meaningful Impact</h4>
                      <p>Make a real difference in patients' lives every day</p>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          </Col>
        </Row>

        <div className="section-subheader text-center mb-4">
          <h3>Open Positions</h3>
        </div>

        <Row className="positions-row">
          {positions.map((position) => (
            <Col lg={4} md={6} sm={12} key={position.id} className="mb-4">
              <Card className="position-card h-100">
                <div className="position-image">
                  <img src={position.image} alt={position.title} />
                </div>
                <Card.Body>
                  <Card.Title className="position-title">{position.title}</Card.Title>
                  <Card.Text className="position-description">{position.description}</Card.Text>
                  <div className="position-requirements">
                    <h5>Requirements:</h5>
                    <p>{position.requirements}</p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <div className="application-section" id="apply">
          <div className="section-subheader text-center mb-4 mt-5">
            <h3>Apply Now</h3>
            <p>Fill out the form below to join our team</p>
          </div>

          <Row className="justify-content-center">
            <Col lg={8}>
              <Card className="application-card">
                <Card.Body>
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>First Name</Form.Label>
                          <Form.Control 
                            type="text" 
                            name="firstName" 
                            value={formData.firstName} 
                            onChange={handleChange} 
                            required 
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Last Name</Form.Label>
                          <Form.Control 
                            type="text" 
                            name="lastName" 
                            value={formData.lastName} 
                            onChange={handleChange} 
                            required 
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Email</Form.Label>
                          <Form.Control 
                            type="email" 
                            name="email" 
                            value={formData.email} 
                            onChange={handleChange} 
                            required 
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Phone</Form.Label>
                          <Form.Control 
                            type="tel" 
                            name="phone" 
                            value={formData.phone} 
                            onChange={handleChange} 
                            required 
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label>Position</Form.Label>
                      <Form.Select 
                        name="position" 
                        value={formData.position} 
                        onChange={handleChange} 
                        required
                      >
                        <option value="">Select a position</option>
                        <option value="Physiotherapy">Physiotherapy</option>
                        <option value="Nursing">Nursing</option>
                        <option value="Care Giver">Care Giver</option>
                        <option value="Doctor">Doctor</option>
                        <option value="Lab">Lab</option>
                        <option value="Mother & Baby Care">Mother & Baby Care</option>
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Resume Link</Form.Label>
                      <Form.Control 
                        type="url" 
                        name="resumeLink" 
                        value={formData.resumeLink} 
                        onChange={handleChange} 
                        placeholder="Link to your resume (Google Drive, Dropbox, etc.)" 
                        required 
                      />
                    </Form.Group>

                    <div className="text-center mt-4">
                      <Button type="submit" className="apply-btn">Submit Application</Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      </Container>
    </div>
  );
};

export default Careers;